import React from "react";

import { AlertTriangle, PenLine } from "lucide-react";

import { PlanCard } from "./PlanCard";
import { ComingSoon } from "./shared";
import { useAccountPlanFile } from "../../Hooks/useAccountPlan";

interface Props {
  accountId: string;
  currentFileId: string | null;
  /** AE gets the (coming soon) tag editor. */
  canEdit: boolean;
}

const MEDDPICC_LABELS: Record<string, string> = {
  EB: "Economic Buyer",
  DM: "Decision Maker",
  CH: "Champion",
  EX: "Executive",
  IN: "Influencer",
  TB: "Technical Buyer",
  PR: "Procurement",
  IT: "IT Stakeholder",
  EU: "End User",
  NA: "Not Applicable",
};

/** Account-level MEDDPICC detail from the current version's Sponsor View. */
const PlanMeddpiccCard: React.FC<Props> = ({
  accountId,
  currentFileId,
  canEdit,
}) => {
  const { file, error, reload } = useAccountPlanFile(accountId, currentFileId);
  const people = file?.sponsor_view?.stakeholders || [];
  // A load failure is shown, not mistaken for "no stakeholders" (hidden).
  if (!file && error)
    return (
      <PlanCard title="MEDDPICC detail">
        <p
          role="alert"
          className="flex items-center gap-2 text-sm text-red-600"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
          Couldn&apos;t load the stakeholders.
          <button
            type="button"
            onClick={() => void reload()}
            className="font-medium text-red-700 underline underline-offset-2"
          >
            Try again
          </button>
        </p>
      </PlanCard>
    );
  if (!people.length) return null;

  const codes: Record<string, boolean> = {};
  people.forEach(p => (p.meddpicc || []).forEach(c => (codes[c] = true)));
  const tagged = people.filter(p => (p.meddpicc || []).length > 0).length;
  const gaps: string[] = [];
  if (!codes.EB) gaps.push("Economic Buyer");
  if (!codes.CH) gaps.push("Champion");

  return (
    <PlanCard
      title="MEDDPICC detail"
      subtitle={
        <>
          {tagged}/{people.length} stakeholders tagged ·{" "}
          {gaps.length ? (
            <span className="font-semibold text-rose-600">
              missing a confirmed {gaps.join(" and ")}
            </span>
          ) : (
            <span className="font-semibold text-emerald-700">
              Economic Buyer and Champion confirmed
            </span>
          )}
        </>
      }
      aside={
        canEdit ? (
          <ComingSoon
            label="Edit tags"
            icon={PenLine}
            title="Confirming or re-tagging stakeholders comes later; changes will land in the next draft briefing."
          />
        ) : undefined
      }
    >
      <ul className="divide-y divide-gray-100">
        {people.map((p, i) => (
          <li
            key={`${p.name}-${i}`}
            className="flex items-center justify-between gap-3 py-2"
          >
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-800">{p.name}</div>
              {p.title && (
                <div className="text-[11px] text-gray-500">{p.title}</div>
              )}
            </div>
            <div className="flex flex-wrap justify-end gap-1">
              {(p.meddpicc || []).length ? (
                p.meddpicc.map(m => (
                  <span
                    key={m}
                    title={`MEDDPICC: ${MEDDPICC_LABELS[m] || m}`}
                    className="rounded border border-gray-300 px-1 font-mono text-[10px] font-semibold text-gray-600"
                  >
                    {m}
                  </span>
                ))
              ) : (
                <span className="text-[11px] text-gray-400">untagged</span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </PlanCard>
  );
};

export default PlanMeddpiccCard;
